/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { Table } from 'src/core/components/Table';
import { TableProps } from 'src/core/components/TableProps';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ResourceAssignmentStore } from 'src/core/stores/ResourceAssignmentStore';

export type RegionDeploymentsTableProps = TableProps<ResourceAssignmentStore>;

const messages = defineMessages({
    region: {
        id: 'modelDeploy.regionDeploymentsTable.region',
        defaultMessage: 'Region'
    },
    subscription: {
        id: 'modelDeploy.regionDeploymentsTable.subscription',
        defaultMessage: 'Subscription'
    },
    resource: {
        id: 'modelDeploy.regionDeploymentsTable.resource',
        defaultMessage: 'Resource'
    },
    emptyStateMessage: {
        id: 'modelDeployLayout.regionDeploymentsTable.emptyStateMessage',
        defaultMessage: 'No Resource Assignments available.'
    },
    notAssignedSubscription: {
        id: 'modelDeploy.regionDeploymentsTable.notAssignedSubscription',
        defaultMessage: 'Not assigned'
    }
});

export const RegionDeploymentsTable = observer((props: RegionDeploymentsTableProps) => {
    const { items, sortKey, selection } = props;
    const { localize } = useLocalization();

    const sortColumnObject = (field: keyof ResourceAssignmentStore) => ({
        isSorted: sortKey?.key === field,
        isSortedDescending: sortKey?.key === field && sortKey?.order === 'desc'
    });

    const columns: IColumn[] = [
        {
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            key: 'regionName',
            fieldName: 'regionName',
            ...sortColumnObject('regionName'),
            name: localize(messages.region),
            onRender: (item: ResourceAssignmentStore) => <LuisText>{item.regionName}</LuisText>
        },
        {
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            key: 'subscriptionName',
            fieldName: 'subscriptionName',
            ...sortColumnObject('subscriptionName'),
            name: localize(messages.subscription),
            onRender: (item: ResourceAssignmentStore) => (
                <LuisText>{item.subscriptionName ?? localize(messages.notAssignedSubscription)}</LuisText>
            )
        },
        {
            minWidth: 200,
            maxWidth: 300,
            isResizable: true,
            key: 'resourceName',
            fieldName: 'resourceName',
            ...sortColumnObject('resourceName'),
            name: localize(messages.resource),
            onRender: (item: ResourceAssignmentStore) => <LuisText>{item.resourceName}</LuisText>
        }
    ];

    return (
        <Table<ResourceAssignmentStore>
            items={items}
            columns={columns}
            selection={selection}
            selectionMode={SelectionMode.single}
            emptyPlaceholder={localize(messages.emptyStateMessage)}
            {...props}
        />
    );
});
